import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../config/authConfig';
import { InteractionStatus } from '@azure/msal-browser';
import authService from '../services/authService';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: any;
  signIn: () => Promise<void>;
  signInGuest: (username: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [user, setUser] = useState<any>(null);
  const [guestUser, setGuestUser] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  // Check for existing authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if user has stored auth token (RBAC login)
        const storedUser = authService.getUser();
        const token = authService.getToken();
        
        if (storedUser && token) {
          console.log('Found stored user, verifying token...');
          // Verify token is still valid
          const isValid = await authService.verifyToken();
          if (isValid) {
            // Ensure stored user has the required properties
            const restoredUser = {
              ...storedUser,
              isGuest: true,
              loginType: 'rbac',
            };
            setGuestUser(restoredUser);
            console.log('Token verified, user authenticated');
          } else {
            console.log('Token invalid, clearing auth data');
            authService.clearAuthData();
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        authService.clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const isAuthenticated = accounts.length > 0 || guestUser !== null;
  const isLoading = inProgress !== InteractionStatus.None || loading;

  useEffect(() => {
    // Set user from MSAL accounts or guest user
    // Prioritize guest user if it exists (most recent RBAC login)
    if (guestUser) {
      setUser(guestUser);
    } else if (accounts.length > 0) {
      setUser(accounts[0]);
    } else {
      setUser(null);
    }
  }, [accounts, guestUser]);

  const signIn = async () => {
    try {
      // Clear any existing guest user session first
      if (guestUser) {
        console.log('Clearing existing guest user session...');
        setGuestUser(null);
        authService.clearAuthData();
      }
      
      // Trigger Azure login popup
      const loginResponse = await instance.loginPopup(loginRequest);
      
      console.log('Azure login successful, extracting id_token...');
      
      // Extract id_token from login response
      const idToken = loginResponse.idToken;
      
      if (!idToken) {
        throw new Error('No id_token received from Azure login');
      }
      
      console.log('Sending id_token to backend for authentication...');
      
      // Send id_token to backend for validation and JWT generation
      const backendResponse = await authService.azureLogin(idToken);
      
      console.log('Backend authentication successful:', backendResponse.data.user);
      
      // Set user from backend response (not from MSAL)
      const azureUserData = {
        username: backendResponse.data.user.username,
        name: backendResponse.data.user.username,
        role: backendResponse.data.user.role,
        team: backendResponse.data.user.team,
        actions: backendResponse.data.user.actions,
        isGuest: false,
        loginType: 'azure',
      };
      
      setGuestUser(azureUserData);
      
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const signInGuest = async (username: string, password: string) => {
    try {
      console.log('Attempting guest login via backend RBAC...');
      
      // Clear any existing Microsoft session first to prevent conflicts
      if (accounts.length > 0) {
        console.log('Clearing existing Microsoft session...');
        try {
          await instance.logoutPopup({
            postLogoutRedirectUri: window.location.origin,
          });
        } catch (msalError) {
          console.warn('Failed to clear MSAL session, continuing with login:', msalError);
        }
      }
      
      // Call backend authentication service
      const response = await authService.login(username, password);
      
      console.log('Backend authentication successful:', response.data.user);
      
      // Set guest user from backend response
      const guestUserData = {
        username: response.data.user.username,
        name: response.data.user.username,
        role: response.data.user.role,
        team: response.data.user.team,
        actions: response.data.user.actions,
        isGuest: true,
        loginType: 'rbac',
      };
      
      setGuestUser(guestUserData);
    } catch (error: any) {
      console.error('Guest login failed:', error);
      authService.clearAuthData();
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear guest user if exists
      if (guestUser) {
        setGuestUser(null);
        authService.logout();
      }
      
      // Logout from Azure AD if authenticated
      if (accounts.length > 0) {
        await instance.logoutPopup({
          postLogoutRedirectUri: '/',
        });
      }
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    signIn,
    signInGuest,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;